import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  alpha: number;
}

const LINK_DISTANCE = 150;
const PRIMARY_COLOR = 'hsl(248, 32%, 62%)';

function randomBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

function createParticles(width: number, height: number): Particle[] {
  // 约 80–120 个粒子，根据屏幕尺寸自适应
  const count = Math.max(80, Math.min(120, Math.round((width * height) / 16000)));
  return Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: randomBetween(0.2, 0.5) * (Math.random() < 0.5 ? -1 : 1),
    vy: randomBetween(0.2, 0.5) * (Math.random() < 0.5 ? -1 : 1),
    r: randomBetween(1.5, 2),
    alpha: randomBetween(0.15, 0.4),
  }));
}

export function HeroBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let rafId = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = createParticles(width, height);
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // 连线
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;
          if (distSq > LINK_DISTANCE * LINK_DISTANCE) continue;
          const dist = Math.sqrt(distSq);
          const alpha = (1 - dist / LINK_DISTANCE) * 0.35;
          ctx.strokeStyle = PRIMARY_COLOR;
          ctx.globalAlpha = alpha;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // 粒子
      for (const p of particles) {
        ctx.fillStyle = PRIMARY_COLOR;
        ctx.globalAlpha = p.alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    };

    const update = () => {
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }
    };

    const loop = () => {
      if (!reduceMotion) update();
      draw();
      rafId = requestAnimationFrame(loop);
    };

    resize();
    draw();

    if (!reduceMotion) {
      rafId = requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-background" aria-hidden>
      {/* 底层：左上冷色径向渐变光晕 */}
      <div className="absolute left-1/2 top-[30%] h-[400px] w-[min(80%,600px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,_hsl(248_32%_62%_/_0.08)_0%,_transparent_70%)]" />
      {/* 第二层：右下暖色光晕，与左上冷色形成对比 */}
      <div className="absolute bottom-[-10%] right-[-10%] h-[360px] w-[360px] rounded-full bg-[radial-gradient(ellipse_at_center,_hsl(30_60%_50%_/_0.04)_0%,_transparent_70%)]" />
      {/* 动态粒子网格层 */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
